import React, { useMemo } from 'react';
import { arrayUtils, DropdownOption } from '@sato/components';
import { uniqBy } from 'lodash';
import { observer } from 'mobx-react-lite';

import { HookFormDropdown } from '@components/ReactHookFormFields/HookFormDropdown';
import { FacilityType } from '@models/realEstate.types';
import { apartmentStore } from '@stores/apartmentStore';
import { t } from '@stores/localization/localization.store';
import { getFeatureLocationTitle } from '@utils/apartmentUtils';

/** Features we don't want to show in the dropdown */
const FEATURE_DENY_LIST = ['MANGLE'] satisfies FacilityType[];

export const CommonSpacesDropdown = observer(
  ({ name, label }: { name: string; label: string }) => {
    const selectedRealEstate = apartmentStore.selectedApartment?.realestate;

    const options = useMemo<DropdownOption[]>(() => {
      const featureLocations =
        selectedRealEstate?.realEstateFeatureLocations.filter(
          ({ feature }) => !FEATURE_DENY_LIST.some((x) => x === feature)
        );

      const featureLocationOptions = featureLocations?.map<DropdownOption>(
        ({ location, feature }) => {
          const label = arrayUtils.joinArrayString([
            t(`feature.${feature}`),
            getFeatureLocationTitle(location, t),
          ]);

          return {
            label,
            value: label,
          };
        }
      );
      const stairwells = selectedRealEstate?.buildings?.flatMap(
        (building) => building.stairwells
      );

      const stairwellLocationOptions = stairwells?.map(({ name }) => {
        const label = `${t('apartment_stairwell_keyword')} ${name.replace(/porraskäytävä/i, '')}`;

        return {
          label,
          value: label,
        };
      });

      return uniqBy<DropdownOption>(
        [
          ...(featureLocationOptions ?? []),
          ...(stairwellLocationOptions ?? []),
          {
            label: t('dropdownSelectionOther'),
            value: t('dropdownSelectionOther'),
          },
        ],
        // Filter out possible duplicate values
        (option) => option.value
      );
    }, [selectedRealEstate]);

    return (
      <HookFormDropdown
        name={name}
        label={label}
        floatingLabel
        items={options}
      />
    );
  }
);
